import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";

const DEFAULT_CARTOON_PARAMS = {
  ageGroup: "PRESCHOOL",
  gender: 1,
  skinTone: "",
  hairColor: "",
  hairStyle: "",
  eyeColor: "",
};

const FALLBACK_OPTIONS = {
  ageGroup: [
    { label: "Infant", subtitle: "(0-1)", value: "INFANT", image: "/path/to/infant.png" },
    { label: "Toddler", subtitle: "(1-3)", value: "TODDLER", image: "/path/to/toddler.png" },
    { label: "Preschool", subtitle: "(3-5)", value: "PRESCHOOL", image: "/path/to/preschool.png" },
    { label: "Pre-K", subtitle: "(4-5)", value: "PREK", image: "/path/to/prek.png" },
  ],
  gender: [
    { label: "Boy", value: 1, image: "/path/to/boy.png" },
    { label: "Girl", value: 0, image: "/path/to/girl.png" },
  ],
  skinTone: [
    { label: "Caucasian", value: "CAUCASIAN", image: "/path/to/light.png" },
    { label: "Indian", value: "INDIAN", image: "/path/to/light.png" },
    { label: "Asian", value: "ASIAN", image: "/path/to/light.png" },
    { label: "African American", value: "AFRICAN_AMERICAN", image: "/path/to/light.png" },
    { label: "Mixed", value: "MIX", image: "/path/to/light.png" },
  ],
  hairColor: [
    { label: "Black", value: "BLACK", image: "/path/to/light.png" },
    { label: "Brunette", value: "BRUNETTE", image: "/path/to/light.png" },
    { label: "Blonde", value: "BLONDE", image: "/path/to/light.png" },
    { label: "Brown", value: "BROWN", image: "/path/to/light.png" },
  ],
  hairStyle: [
    { label: "Short", value: "SHORT", image: "/path/to/style-short.png" },
    { label: "Long", value: "LONG", image: "/path/to/style-long.png" },
  ],
  eyeColor: [
    { label: "Brown", value: "BROWN", image: "/path/to/eye-hazel.png" },
    { label: "Blue", value: "BLUE", image: "/path/to/eye-hazel.png" },
    { label: "Hazel", value: "HAZEL", image: "/path/to/eye-hazel.png" },
    { label: "Gray", value: "GRAY", image: "/path/to/eye-hazel.png" },
  ],
};

const normalize = (value) => String(value ?? "").trim().toLowerCase();

const getCartoonId = (cartoon) =>
  cartoon?.id ?? cartoon?._id ?? cartoon?.cartoon_id ?? cartoon?.cartoonId ?? null;

const getCartoonField = (cartoon, snakeKey, camelKey) =>
  cartoon?.[snakeKey] ?? cartoon?.[camelKey];

export function PanelCartoon({
  child,
  onSave,
  bookConfig,
  userId: userIdProp,
  projectId: projectIdProp,
}) {
  const matcherRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Resolve persistence identifiers.
  // Prefer explicit props. The fallbacks keep this component backward-compatible.
  // ---------------------------------------------------------------------------
  const userId =
    userIdProp ??
    child?.user_id ??
    child?.userId ??
    child?.parent_user_id ??
    child?.parentUserId ??
    bookConfig?.user_id ??
    bookConfig?.userId ??
    null;

  const projectId =
    projectIdProp ??
    bookConfig?.project_id ??
    bookConfig?.projectId ??
    bookConfig?.id ??
    bookConfig?._id ??
    child?.project_id ??
    child?.projectId ??
    null;

  // ---------------------------------------------------------------------------
  // UI state
  // ---------------------------------------------------------------------------
  const [selectedImage, setSelectedImage] = useState(child?.cartoon_url || null);
  const [isAiRecommended, setIsAiRecommended] = useState(true);

  const [params, setParams] = useState(DEFAULT_CARTOON_PARAMS);

  // Complete list returned by GET /api/cartoons.
  // This is the ONLY list used for matching/filtering.
  const [allCartoons, setAllCartoons] = useState([]);

  // Options returned by GET /api/cartoon/icons.
  const [iconSections, setIconSections] = useState({});

  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingSelection, setIsLoadingSelection] = useState(false);
  const [isSavingSelection, setIsSavingSelection] = useState(false);

  // Becomes true after the remembered DB state was loaded for this user/project.
  const [selectionReady, setSelectionReady] = useState(false);

  // Incremented ONLY by actual user selection changes.
  // DB hydration does not increment it, so opening the screen does not immediately
  // write the same data back to PostgreSQL.
  const [selectionChangeVersion, setSelectionChangeVersion] = useState(0);

  const loadedSelectionKeyRef = useRef(null);
  const saveRequestSequenceRef = useRef(0);

  const theme = {
    bg: { elevated: "#ffffff", base: "#f9fafb", hover: "#f3f4f6" },
    border: { subtle: "#e5e7eb", default: "#e5e7eb" },
    text: { primary: "#111827", secondary: "#4b5563", muted: "#6b7280" },
    primary: "#6366f1",
    primaryLight: "#eef2ff",
  };

  const isViewOnly = useMemo(
    () => false, // Replace with: isDeadlinePassed(bookConfig?.content_deadline)
    [bookConfig?.content_deadline]
  );

  // ---------------------------------------------------------------------------
  // API #1 + #2
  // Screen mount:
  //   - getCartoons() ONCE, with NO filter params.
  //   - getCartoonIcons() ONCE.
  //
  // Clicking Age/Gender/Skin/Hair/Eye NEVER runs this effect.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let active = true;

    const loadScreenData = async () => {
      setIsFetching(true);

      const [cartoonsResult, iconsResult] = await Promise.allSettled([
        api.getCartoons(),
        api.getCartoonIcons(),
      ]);

      if (!active) return;

      if (cartoonsResult.status === "fulfilled") {
        const fetched = Array.isArray(cartoonsResult.value?.cartoons)
          ? cartoonsResult.value.cartoons
          : [];

        setAllCartoons(fetched);

        if (!selectedImage && fetched.length > 0) {
          setSelectedImage(fetched[0]?.url || null);
        }
      } else {
        console.error("Failed to load cartoons:", cartoonsResult.reason);
        setAllCartoons([]);
      }

      if (iconsResult.status === "fulfilled") {
        setIconSections(iconsResult.value?.sections || {});
      } else {
        // UI remains usable through FALLBACK_OPTIONS.
        console.error("Failed to load cartoon icons:", iconsResult.reason);
        setIconSections({});
      }

      setIsFetching(false);
    };

    loadScreenData();

    return () => {
      active = false;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // API #3
  // Load remembered PostgreSQL selection once for the current user/project.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!userId || !projectId) {
      setSelectionReady(true);
      return;
    }

    const selectionKey = `${String(userId)}::${String(projectId)}`;

    if (loadedSelectionKeyRef.current === selectionKey) {
      return;
    }

    loadedSelectionKeyRef.current = selectionKey;

    let active = true;

    const loadSavedSelection = async () => {
      setIsLoadingSelection(true);
      setSelectionReady(false);

      try {
        const response = await api.getCartoonSelection(userId, projectId);

        if (!active) return;

        const savedSelections = response?.data?.selections || {};
        const matchedCartoon = response?.data?.matchedCartoon || null;

        // Only overwrite fields that actually exist in DB.
        setParams((previous) => ({
          ...previous,
          ...(savedSelections.ageGroup != null
            ? { ageGroup: savedSelections.ageGroup }
            : {}),
          ...(savedSelections.gender != null
            ? {
                gender:
                  savedSelections.gender === "0" || savedSelections.gender === 0
                    ? 0
                    : savedSelections.gender === "1" || savedSelections.gender === 1
                      ? 1
                      : savedSelections.gender,
              }
            : {}),
          ...(savedSelections.skinTone != null
            ? { skinTone: savedSelections.skinTone }
            : {}),
          ...(savedSelections.hairColor != null
            ? { hairColor: savedSelections.hairColor }
            : {}),
          ...(savedSelections.hairStyle != null
            ? { hairStyle: savedSelections.hairStyle }
            : {}),
          ...(savedSelections.eyeColor != null
            ? { eyeColor: savedSelections.eyeColor }
            : {}),
        }));

        if (matchedCartoon?.url) {
          setSelectedImage(matchedCartoon.url);
          setIsAiRecommended(true);
        }

        // DB restore is not a user change, so keep version at zero.
        setSelectionChangeVersion(0);
      } catch (error) {
        console.error("Failed to load saved cartoon selection:", error);
      } finally {
        if (active) {
          setSelectionReady(true);
          setIsLoadingSelection(false);
        }
      }
    };

    loadSavedSelection();

    return () => {
      active = false;
    };
  }, [userId, projectId]);

  // ---------------------------------------------------------------------------
  // LOCAL FILTERING ONLY
  //
  // No API call exists in this memo.
  // Every selection filters the already-loaded allCartoons array in memory.
  // ---------------------------------------------------------------------------
  const filteredCartoons = useMemo(() => {
    if (!allCartoons.length) return [];

    return allCartoons.filter((cartoon) => {
      const cartoonAgeGroup = getCartoonField(cartoon, "age_group", "ageGroup");
      const cartoonGender = cartoon?.gender;

      // Current cartoons_mongo schema calls this field `race`.
      // skin_tone fallback is retained for compatibility.
      const cartoonSkinTone =
        cartoon?.race ??
        cartoon?.skin_tone ??
        cartoon?.skinTone;

      const cartoonHairColor = getCartoonField(cartoon, "hair_color", "hairColor");
      const cartoonHairStyle = getCartoonField(cartoon, "hair_style", "hairStyle");
      const cartoonEyeColor = getCartoonField(cartoon, "eye_color", "eyeColor");

      if (
        params.ageGroup &&
        cartoonAgeGroup != null &&
        normalize(cartoonAgeGroup) !== normalize(params.ageGroup)
      ) {
        return false;
      }

      if (
        params.gender !== "" &&
        params.gender != null &&
        cartoonGender != null &&
        Number(cartoonGender) !== Number(params.gender)
      ) {
        return false;
      }

      if (
        params.skinTone &&
        cartoonSkinTone != null &&
        normalize(cartoonSkinTone) !== normalize(params.skinTone)
      ) {
        return false;
      }

      if (
        params.hairColor &&
        cartoonHairColor != null &&
        normalize(cartoonHairColor) !== normalize(params.hairColor)
      ) {
        return false;
      }

      if (
        params.hairStyle &&
        cartoonHairStyle != null &&
        normalize(cartoonHairStyle) !== normalize(params.hairStyle)
      ) {
        return false;
      }

      if (
        params.eyeColor &&
        cartoonEyeColor != null &&
        normalize(cartoonEyeColor) !== normalize(params.eyeColor)
      ) {
        return false;
      }

      return true;
    });
  }, [allCartoons, params]);

  const matchedCartoon = filteredCartoons[0] || null;

  // ---------------------------------------------------------------------------
  // Preview follows the LOCAL matched cartoon.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!allCartoons.length) return;

    if (matchedCartoon?.url) {
      setSelectedImage(matchedCartoon.url);
      setIsAiRecommended(true);
    } else {
      setSelectedImage(null);
    }
  }, [matchedCartoon, allCartoons.length]);

  // ---------------------------------------------------------------------------
  // API #4
  // Debounced PostgreSQL save.
  //
  // IMPORTANT:
  // - Runs only after an actual user selection.
  // - Does NOT call getCartoons.
  // - matchedCartoon was calculated locally above.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!selectionReady) return;
    if (!selectionChangeVersion) return;
    if (!userId || !projectId) return;
    if (isViewOnly) return;

    const requestSequence = ++saveRequestSequenceRef.current;

    const timer = window.setTimeout(async () => {
      const cartoonId = getCartoonId(matchedCartoon);

      setIsSavingSelection(true);

      try {
        await api.saveCartoonSelection({
          userId: String(userId),
          projectId: String(projectId),
          selections: {
            ageGroup: params.ageGroup ?? "",
            gender: params.gender ?? "",
            skinTone: params.skinTone ?? "",
            hairColor: params.hairColor ?? "",
            hairStyle: params.hairStyle ?? "",
            eyeColor: params.eyeColor ?? "",
          },
          matchedCartoon: cartoonId
            ? {
                id: String(cartoonId),
                url: matchedCartoon?.url || null,
              }
            : null,
          // If local filtering finds nothing, also remove the old DB match.
          clearMatchedCartoon: !cartoonId,
        });

        // Optional parent notification after DB save.
        // if (typeof onSave === "function") {
        //   onSave({
        //     selections: params,
        //     matchedCartoon: matchedCartoon || null,
        //   });
        // }
      } catch (error) {
        console.error("Failed to save cartoon selection:", error);
      } finally {
        // Avoid an older request changing loading state after a newer request started.
        if (requestSequence === saveRequestSequenceRef.current) {
          setIsSavingSelection(false);
        }
      }
    }, 400);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    selectionChangeVersion,
    selectionReady,
    userId,
    projectId,
    isViewOnly,
    params,
    matchedCartoon,
    onSave,
  ]);

  // ---------------------------------------------------------------------------
  // User selection: state only.
  // NO cartoon API call here.
  // ---------------------------------------------------------------------------
  const handleMatcherChange = (field, value) => {
    if (isViewOnly) return;

    setParams((previous) => ({
      ...previous,
      [field]: value,
    }));

    setSelectionChangeVersion((version) => version + 1);
  };

  // ---------------------------------------------------------------------------
  // API #5
  // Reset local UI + delete remembered state from PostgreSQL.
  // ---------------------------------------------------------------------------
  const resetMatcher = async () => {
    if (isViewOnly) return;

    // Cancel any pending debounced save logically.
    saveRequestSequenceRef.current += 1;

    setParams(DEFAULT_CARTOON_PARAMS);
    setSelectionChangeVersion(0);
    setIsAiRecommended(true);

    // Preview will be recalculated locally by filteredCartoons.
    if (!userId || !projectId) return;

    try {
      await api.deleteCartoonSelection(userId, projectId);
    } catch (error) {
      console.error("Failed to reset saved cartoon selection:", error);
    }
  };

  // ---------------------------------------------------------------------------
  // Options
  // Prefer PostgreSQL cartoon_icons. Fall back to existing local arrays when
  // image_url/options are unavailable so the view never becomes unusable.
  // ---------------------------------------------------------------------------
  const getOptions = (sectionKey) => {
    const dbOptions = iconSections?.[sectionKey];

    if (Array.isArray(dbOptions) && dbOptions.length > 0) {
      return dbOptions.map((option) => {
        // Preserve local image placeholders when DB image_url is currently NULL.
        const fallback = FALLBACK_OPTIONS[sectionKey]?.find(
          (item) => normalize(item.value) === normalize(option.value)
        );

        return {
          ...option,
          image: option.image || fallback?.image || null,
        };
      });
    }

    return FALLBACK_OPTIONS[sectionKey] || [];
  };

  const sanitizeOptions = (optionsArray) => {
    if (!Array.isArray(optionsArray)) return [];

    return optionsArray.filter((option) => {
      const value = normalize(option?.value);
      const label = normalize(option?.label);

      return value !== "" && value !== "all" && label !== "all";
    });
  };

  // ---------------------------------------------------------------------------
  // Reusable UI
  // ---------------------------------------------------------------------------
  const OptionCard = ({
    option,
    value,
    onSelect,
    wide = false,
    showImage = true,
  }) => {
    const selected = normalize(value) === normalize(option.value);
    const staticImageUrl = option.image || null;

    return (
      <button
        className={`cartoon-option-card${wide ? " cartoon-option-card--wide" : ""}`}
        type="button"
        onClick={() => onSelect(option.value)}
        disabled={isViewOnly}
        style={{
          position: "relative",
          minWidth: wide ? 130 : 80,
          flex: wide ? "1 1 130px" : "1 1 0",
          minHeight: wide ? 60 : 96,
          padding: wide ? "8px 16px" : "10px 4px",
          display: "flex",
          flexDirection: wide ? "row" : "column",
          alignItems: "center",
          justifyContent: "center",
          gap: wide ? 12 : 6,
          borderRadius: 16,
          border: `1.5px solid ${
            selected ? theme.primary : theme.border.default
          }`,
          background: selected ? theme.primaryLight : theme.bg.elevated,
          color: theme.text.primary,
          cursor: isViewOnly ? "not-allowed" : "pointer",
          transition: "all 0.15s ease",
          boxShadow: selected ? "none" : "0 1px 2px rgba(0,0,0,0.02)",
        }}
      >
        {showImage && (
          <div
            style={{
              width: wide ? 40 : 48,
              height: wide ? 40 : 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {staticImageUrl ? (
              <img
                src={staticImageUrl}
                alt={option.label}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <div
                style={{
                  width: 40,
                  height: 40,
                  background: theme.bg.hover,
                  borderRadius: "50%",
                }}
              />
            )}
          </div>
        )}

        <div style={{ minWidth: 0, textAlign: "center" }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: theme.text.primary,
              lineHeight: 1.2,
            }}
          >
            {option.label}
          </div>

          {option.subtitle && (
            <div
              style={{
                marginTop: 2,
                fontSize: 10,
                color: theme.text.muted,
              }}
            >
              {option.subtitle}
            </div>
          )}
        </div>

        {selected && (
          <span
            style={{
              position: "absolute",
              top: -6,
              right: -6,
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: theme.primary,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              border: "2px solid #fff",
            }}
          >
            <span style={{ fontSize: 10, fontWeight: "bold" }}>✓</span>
          </span>
        )}
      </button>
    );
  };

  const Section = ({ number, title, children, right }) => (
    <div
      className="cartoon-section"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        className="cartoon-section-heading"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: theme.primary,
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {number}
          </span>

          <span
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: theme.text.primary,
            }}
          >
            {title}
          </span>
        </div>

        {right}
      </div>

      {children}
    </div>
  );

  const ageOptions = sanitizeOptions(getOptions("ageGroup"));
  const genderOptions = sanitizeOptions(getOptions("gender"));
  const skinToneOptions = sanitizeOptions(getOptions("skinTone"));
  const hairColorOptions = sanitizeOptions(getOptions("hairColor"));
  const hairStyleOptions = sanitizeOptions(getOptions("hairStyle"));
  const eyeColorOptions = sanitizeOptions(getOptions("eyeColor"));

  const loading = isFetching || isLoadingSelection;

  return (
    <div className="panel-cartoon-root">
      <style>{`
        .panel-cartoon-root {
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }

        .visual-matcher-drawer,
        .visual-matcher-drawer * {
          box-sizing: border-box;
        }

        .cartoon-option-card {
          max-width: 100%;
        }

        @media (max-width: 900px) {
          .visual-matcher-drawer {
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 24px !important;
            padding: 20px !important;
          }

          .live-preview-column {
            position: relative !important;
            top: auto !important;
            width: 100%;
            max-width: 480px;
            margin: 0 auto;
          }

          .cartoon-attributes-column {
            gap: 28px !important;
          }

          .cartoon-option-grid {
            display: grid !important;
            grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
            gap: 12px !important;
          }

          .cartoon-option-card {
            width: 100% !important;
            min-width: 0 !important;
            flex: none !important;
          }
        }

        @media (max-width: 600px) {
          .visual-matcher-drawer {
            gap: 22px !important;
            padding: 14px !important;
            border-radius: 16px !important;
            box-shadow: 0 4px 20px rgba(0,0,0,0.05) !important;
          }

          .live-preview-column {
            max-width: none;
          }

          .cartoon-preview-box {
            min-height: 220px !important;
          }

          .cartoon-attributes-column {
            gap: 24px !important;
          }

          .cartoon-section {
            gap: 10px !important;
          }

          .cartoon-section-heading {
            gap: 8px;
            flex-wrap: wrap;
          }

          .cartoon-option-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px !important;
          }

          .cartoon-option-card {
            min-height: 90px !important;
            padding: 8px 4px !important;
            border-radius: 14px !important;
          }

          .cartoon-option-card--wide {
            min-height: 68px !important;
            padding: 8px !important;
            gap: 8px !important;
          }
        }

        @media (max-width: 360px) {
          .visual-matcher-drawer {
            padding: 10px !important;
          }

          .cartoon-option-grid {
            gap: 8px !important;
          }

          .cartoon-option-card {
            min-height: 86px !important;
          }

          .cartoon-option-card--wide {
            flex-direction: column !important;
            min-height: 86px !important;
          }
        }
      `}</style>

      <div
        ref={matcherRef}
        className="visual-matcher-drawer"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(210px, 280px) minmax(0, 1fr)",
          gap: 32,
          padding: 24,
          borderRadius: 24,
          background: theme.bg.elevated,
          boxShadow: "0 10px 40px rgba(0,0,0,0.06)",
        }}
      >
        {/* COLUMN 1: Live Preview */}
        <div
          className="live-preview-column"
          style={{
            display: "flex",
            flexDirection: "column",
            position: "sticky",
            top: 24,
            height: "fit-content",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: theme.text.primary,
              }}
            >
              Live Preview
            </span>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {isSavingSelection && (
                <span
                  style={{
                    fontSize: 10,
                    color: theme.text.muted,
                  }}
                >
                  Saving...
                </span>
              )}

              <button
                type="button"
                onClick={resetMatcher}
                title="Reset Filters"
                disabled={isViewOnly}
                style={{
                  background: "transparent",
                  border: "none",
                  color: isViewOnly
                    ? theme.text.muted
                    : theme.text.secondary,
                  cursor: isViewOnly ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 12,
                }}
              >
                Reset
              </button>
            </div>
          </div>

          <div
            className="cartoon-preview-box"
            style={{
              minHeight: 260,
              borderRadius: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              position: "relative",
              background: selectedImage
                ? `url(${selectedImage}) center/contain no-repeat`
                : theme.bg.base,
              border: `1px solid ${theme.border.subtle}`,
              opacity: loading ? 0.6 : 1,
              transition: "opacity 0.2s ease",
            }}
          >
            {selectedImage && isAiRecommended && (
              <div
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  background: "#FFB800",
                  color: "#000",
                  padding: "4px 10px",
                  borderRadius: 12,
                  fontSize: 10,
                  fontWeight: 700,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  zIndex: 3,
                }}
              >
                AI Match
              </div>
            )}

            {loading && (
              <div
                style={{
                  position: "absolute",
                  zIndex: 10,
                }}
              >
                <span
                  style={{
                    color: theme.primary,
                    fontWeight: "bold",
                  }}
                >
                  Loading...
                </span>
              </div>
            )}

            {!selectedImage && !loading && (
              <div
                style={{
                  color: theme.text.muted,
                  fontSize: 12,
                }}
              >
                No match found
              </div>
            )}
          </div>

          {!userId || !projectId ? (
            <div
              style={{
                marginTop: 8,
                fontSize: 10,
                lineHeight: 1.4,
                color: theme.text.muted,
              }}
            >
              Local matching is active. Pass userId and projectId to enable
              PostgreSQL selection memory.
            </div>
          ) : null}
        </div>

        {/* COLUMN 2: Attributes */}
        <div
          className="cartoon-attributes-column"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 32,
            minWidth: 0,
            opacity: isViewOnly ? 0.6 : 1,
            pointerEvents: isViewOnly ? "none" : "auto",
          }}
        >
          {/* 1. Age */}
          <Section
            number="1"
            title="Age"
            // right={
            //   <span
            //     style={{
            //       fontSize: 12,
            //       color: theme.primary,
            //       cursor: "pointer",
            //       fontWeight: 500,
            //     }}
            //   >
            //     Why we ask? ⓘ
            //   </span>
            // }
          >
            <div
              className="cartoon-option-grid"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              {ageOptions.map((option) => (
                <OptionCard
                  key={String(option.value)}
                  option={option}
                  value={params.ageGroup}
                  onSelect={(value) =>
                    handleMatcherChange("ageGroup", value)
                  }
                />
              ))}
            </div>
          </Section>

          {/* 2. Gender */}
          <Section number="2" title="Boy or Girl">
            <div
              className="cartoon-option-grid"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              {genderOptions.map((option) => (
                <OptionCard
                  key={String(option.value)}
                  option={option}
                  value={params.gender}
                  onSelect={(value) =>
                    handleMatcherChange(
                      "gender",
                      value === "0" || value === 0 ? 0 : 1
                    )
                  }
                  wide
                />
              ))}
            </div>
          </Section>

          {/* 3. Skin Tone */}
          <Section number="3" title="Skin tone">
            <div
              className="cartoon-option-grid"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              {skinToneOptions.map((option) => (
                <OptionCard
                  key={String(option.value)}
                  option={option}
                  value={params.skinTone}
                  onSelect={(value) =>
                    handleMatcherChange("skinTone", value)
                  }
                />
              ))}
            </div>
          </Section>

          {/* 4. Hair */}
          <Section number="4" title="Hair">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 13,
                    color: theme.text.secondary,
                    marginBottom: 8,
                    fontWeight: 500,
                  }}
                >
                  Hair color
                </div>

                <div
                  className="cartoon-option-grid"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                  }}
                >
                  {hairColorOptions.map((option) => (
                    <OptionCard
                      key={String(option.value)}
                      option={option}
                      value={params.hairColor}
                      onSelect={(value) =>
                        handleMatcherChange("hairColor", value)
                      }
                    />
                  ))}
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: 13,
                    color: theme.text.secondary,
                    marginBottom: 8,
                    fontWeight: 500,
                  }}
                >
                  Hair style
                </div>

                <div
                  className="cartoon-option-grid"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                  }}
                >
                  {hairStyleOptions.map((option) => (
                    <OptionCard
                      key={String(option.value)}
                      option={option}
                      value={params.hairStyle}
                      onSelect={(value) =>
                        handleMatcherChange("hairStyle", value)
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          </Section>

          {/* 5. Eye Color */}
          <Section number="5" title="Eye Color">
            <div
              className="cartoon-option-grid"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              {eyeColorOptions.map((option) => (
                <OptionCard
                  key={String(option.value)}
                  option={option}
                  value={params.eyeColor}
                  onSelect={(value) =>
                    handleMatcherChange("eyeColor", value)
                  }
                />
              ))}
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}

export default PanelCartoon;
